import { ChannelKeywordSet, MesgFilter, MesgFilterType, MesgFilterInfo, ChatRoomProps } from '@dinosband/bv-main-schema/dist/types'
import { ChatRoomQueryOption, QueryFilter } from '@dinosband/bv-messenger-schema'
import { _isEqualArray } from '@dinosband/dbi-utils'
import { mainStore } from '@dinosband/bv-utils'
import { dataCenter } from '@dinosband/bv-messenger-controller'

export const EMPTY_MESG_FILTER: MesgFilter = {
  version: 2,
  type: MesgFilterType.FILTER,
  keywords: [],
  infoKeywords: [],
  matchKeywords: [[], [], [], [], []],
  locations: [],
}

export function _isFiltered (info?: MesgFilterInfo) {
  if (!info) {
    return false
  }
  if (info.enableFilter && (info.channelFilter || info.customFilter)) {
    return true
  }
  return info.excludeFinished ?? false
}

export function _queryFilter (chatRoomId: number, info?: MesgFilterInfo, opt?: ChatRoomQueryOption): QueryFilter | undefined {
  if (!info && !opt) {
    return
  }

  // chatRoomProps의 inclMatchSets 적용
  const chatRoom = dataCenter.chatRoom(chatRoomId)
  if (!chatRoom) {
    return
  }
  const props = JSON.parse(chatRoom.props ?? '{}') as ChatRoomProps
  const mesgFilter = (info?.enableFilter ? info.useChannelFilter ? info.channelFilter : info.customFilter : undefined) ?? EMPTY_MESG_FILTER
  const filtered = JSON.parse(JSON.stringify(mesgFilter)) as MesgFilter
  const inclSets = props.inclMatchSets
  if (inclSets) {
    const channel = mainStore.$currentChannel
    const sets = JSON.parse(channel.matchKeywords ?? '[]') as ChannelKeywordSet[]
    for (let i = 0; i < sets.length; i++) {
      if (!inclSets.includes(i)) {
        filtered.matchKeywords[i] = []
      }
      // filed specific keyword를 추가
      filtered.matchKeywords[i].push(...filtered.matchKeywords[i].map((k) => `@${i}:${k}`))
    }
  }
  if (props.exclLocationFilter) {
    filtered.locations = []
  }
  if (props.exclTimeFilter) {
    filtered.times = undefined
  }

  const filter: QueryFilter = {
    ...filtered,
    excludeFinished: info?.excludeFinished ?? false,
    excludeDeleted: opt?.excludeDeleted ?? true,
    onlyOwnMessages: opt?.onlyOwnMessages,
    includeByAdmin: opt?.includeByAdmin,
  }

  return filter
}

export const _isEmptyFilter = (filter?: MesgFilter) => {
  if (!filter) {
    return true
  }
  return filter.matchKeywords.flat(9).length === 0 &&
         filter.locations.flat(9).length === 0
}

export const _isEqualFilter = (a?: MesgFilter, b?: MesgFilter) => {
  if (a == null || b == null) {
    return a == b
  }
  return _isEqualArray(a.locations, b.locations) &&
         _isEqualArray(a.keywords, b.keywords) &&
         _isEqualArray(a.infoKeywords, b.infoKeywords) &&
         _isEqualArray(a.matchKeywords, b.matchKeywords)
}

export const _isEqualQueryFilter = (a?: QueryFilter, b?: QueryFilter) => {
  if (a == null || b == null) {
    return a == b
  }
  return _isEqualFilter(a, b) &&
         !a.excludeFinished === !b.excludeFinished &&
         !a.excludeDeleted === !b.excludeDeleted &&
         !a.onlyOwnMessages === !b.onlyOwnMessages &&
         !a.includeByAdmin === !b.includeByAdmin
}

export const _isEqualFilterInfo = (a?: MesgFilterInfo, b?: MesgFilterInfo, exceptEnable?: boolean) => {
  if (a == null || b == null) {
    return a == b
  }
  return (exceptEnable || a.enableFilter === b.enableFilter) &&
         (a.useChannelFilter === b.useChannelFilter) &&
         (a.excludeFinished ?? false) === (b.excludeFinished ?? false) &&
         _isEqualFilter(a.channelFilter, b.channelFilter) &&
         _isEqualFilter(a.customFilter, b.customFilter)
}

export const _logFilter = (filter?: MesgFilter) => {
  if (!filter) {
    return 'undefined'
  }
  const desc = filter ? [...filter.locations, ...filter.matchKeywords].flat(2).filter((i) => i.length) : 'empty'
  return desc
}

export const _logFilterInfo = (info?: MesgFilterInfo) => {
  if (!info) {
    return 'undefined'
  }
  let desc = info.enableFilter ? 'enabled' : 'disabled'
  desc += ' useChannelFilter:' + info.useChannelFilter
  desc += ' excludeFinished:' + info.excludeFinished
  desc += ' channel:' + _logFilter(info.channelFilter)
  desc += ' custom:' + _logFilter(info.customFilter)
  return desc
}

export const _logFilterMap = (filterMap: Record<number, MesgFilterInfo>) => {
  const keys = Object.keys(filterMap).map(Number)
  return keys.reduce((result, key) => {
    result[key] = _logFilterInfo(filterMap[key])
    return result
  }, {} as { [key: number]: string | string[] })
}

export function mergeMesgFilter (a?: MesgFilter | null, b?: MesgFilter | null): MesgFilter | undefined | null {
  if (a == null || b == null) {
    return a || b
  }
  const locations = [...new Set([...a.locations ?? [], ...b.locations ?? []])]
  const keywords = [...new Set([...a.keywords ?? [], ...b.keywords ?? []])]
  const infoKeywords = [...new Set([...a.infoKeywords ?? [], ...b.infoKeywords ?? []])]
  const m_a = a.matchKeywords ?? []
  const m_b = b.matchKeywords ?? []
  const [m1, m2] = m_a.length >= m_b.length ? [m_a, m_b] : [m_b, m_a]
  const matchKeywords = m1.map((keywords, idx) => [...new Set([...keywords, ...m2[idx] ?? []])])
  return {
    locations,
    keywords,
    infoKeywords,
    matchKeywords,
  }
}

export function parseMesgFilter (value: string | undefined | null) {
  if (!value) {
    return undefined
  }
  const filter = JSON.parse(value) as MesgFilter
  if (filter?.locations?.[0] === '--' &&
      filter?.matchKeywords?.[0]?.[0] === '--') {
    return filter
  }
  if (filter?.locations?.[0] === '--') {
    filter.locations = []
  }
  return filter
}

export function createMesgFilterInfo (channelFilter?: MesgFilter, excludeFinished?: boolean, enable = true) {
  const info: MesgFilterInfo = {
    enableFilter: enable,
    useChannelFilter: !!channelFilter,
    channelFilter,
    excludeFinished: excludeFinished ?? false,
  }
  return info
}

export const EMPTY_MESG_FILTER_INFO = createMesgFilterInfo()